/**
 * 다각형 엔진 패널 렌더러 — 화면 배율로 직접 그려 선명, 라벨은 항상 바로 읽히게
 */
import { Hari, Door } from '../types';
import { Pt, PolyPanel, Opening } from './polyEngine';

export interface View { scale: number; ox: number; oy: number } // 화면 = o + mm × scale

const FILL = '#D5D5D5';
const LINE = '#222';
const FONT = '"Malgun Gothic", "Apple SD Gothic Neo", "Noto Sans KR", Arial, sans-serif';

const add = (p: Pt, q: Pt): Pt => ({ x: p.x + q.x, y: p.y + q.y });
const mul = (p: Pt, k: number): Pt => ({ x: p.x * k, y: p.y * k });
const unitOf = (a: Pt, b: Pt): Pt => { const l = Math.hypot(b.x - a.x, b.y - a.y) || 1; return { x: (b.x - a.x) / l, y: (b.y - a.y) / l }; };
const perp = (d: Pt): Pt => ({ x: -d.y, y: d.x });

export function drawPolyPanels(ctx: CanvasRenderingContext2D, panels: PolyPanel[], PT: number, v: View, opts: { labels?: boolean; highlight?: string | null; labelOf?: (p: PolyPanel) => string | undefined } = {}) {
  const S = (p: Pt) => ({ x: v.ox + p.x * v.scale, y: v.oy + p.y * v.scale });
  const TD = PT / 2; // 삼각형 깊이 (mm)
  const lw = Math.max(1, Math.min(2, v.scale * PT * 0.08));
  const poly = (pts: Pt[], fill: string | null) => {
    ctx.beginPath();
    pts.map(S).forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
    ctx.closePath();
    if (fill) { ctx.fillStyle = fill; ctx.fill(); }
    ctx.strokeStyle = LINE; ctx.lineWidth = lw; ctx.stroke();
  };

  // 1) 몸통 (칸막이 등은 자기 두께)
  panels.forEach(p => {
    const d = unitOf(p.a, p.b), n = mul(perp(d), (p.thick ?? PT) / 2);
    poly([add(p.a, n), add(p.b, n), add(p.b, mul(n, -1)), add(p.a, mul(n, -1))], FILL);
  });

  // 2) 결합 삼각형 (몸통 위에 그려야 이웃 몸통에 가려지지 않음)
  panels.forEach(p => {
    const d = unitOf(p.a, p.b), n = mul(perp(d), (p.thick ?? PT) / 2);
    const TD = (p.thick ?? PT) / 2;
    // 끝 양수: 끝면에서 흐름 방향으로 돌출
    if (p.endType === 'yangsu') poly([add(p.b, n), add(p.b, mul(d, TD)), add(p.b, mul(n, -1))], FILL);
    if (p.startType === 'yangsu') poly([add(p.a, n), add(p.a, mul(d, -TD)), add(p.a, mul(n, -1))], FILL);
    // 시작 음수: 시작면에서 안쪽으로 파인 홈
    if (p.startType === 'eumsu') poly([add(p.a, n), add(p.a, mul(d, TD)), add(p.a, mul(n, -1))], FILL);
    if (p.endType === 'eumsu') poly([add(p.b, n), add(p.b, mul(d, -TD)), add(p.b, mul(n, -1))], FILL);
    // 코너: 소유한 칸(패널 시작 PT 구간) 안, 들어오는 패널 쪽 면을 밑변으로 — 들어오는 양수와 한 삼각형으로 겹침
    if (p.startType === 'corner' && p.inDir) {
      const cell = add(p.a, mul(d, PT / 2));
      const face = add(cell, mul(p.inDir, -PT / 2));
      const across = mul(perp(p.inDir), PT / 2);
      poly([add(face, across), add(face, mul(p.inDir, TD)), add(face, mul(across, -1))], FILL);
    }
  });

  // 선택 패널 강조
  const sel = opts.highlight ? panels.find(p => p.id === opts.highlight) : null;
  if (sel) {
    const d = unitOf(sel.a, sel.b), n = mul(perp(d), (sel.thick ?? PT) / 2);
    ctx.save();
    ctx.beginPath();
    [add(sel.a, n), add(sel.b, n), add(sel.b, mul(n, -1)), add(sel.a, mul(n, -1))].map(S).forEach((q, i) => (i ? ctx.lineTo(q.x, q.y) : ctx.moveTo(q.x, q.y)));
    ctx.closePath();
    ctx.fillStyle = 'rgba(229,57,53,0.18)'; ctx.fill();
    ctx.strokeStyle = '#e53935'; ctx.lineWidth = 2.5; ctx.stroke();
    ctx.restore();
  }

  // 3) 라벨 — 패널 축 방향, 항상 바로 읽히게 (가로: 왼→오, 세로: 아래→위)
  if (opts.labels !== false) {
    const ptPx = PT * v.scale;
    const fontPx = Math.max(9, Math.min(15, ptPx * 0.62));
    if (ptPx >= 9) {
      ctx.fillStyle = '#111';
      ctx.font = `600 ${fontPx}px ${FONT}`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      panels.forEach(p => {
        const lenPx = p.size * v.scale;
        const text = opts.labelOf?.(p) ?? `${p.size}`;
        if (ctx.measureText(text).width > lenPx - 4) return;
        const c = S(mul(add(p.a, p.b), 0.5));
        const horiz = Math.abs(p.b.x - p.a.x) > Math.abs(p.b.y - p.a.y);
        ctx.save();
        ctx.translate(Math.round(c.x), Math.round(c.y));
        if (!horiz) ctx.rotate(-Math.PI / 2);
        ctx.fillText(text, 0, 0.5);
        ctx.restore();
      });
    }
  }
}

/** 기존 벽(패널 없는 구간) — 외곽선 바깥쪽에 빗금 띠 */
export function drawExistingWalls(ctx: CanvasRenderingContext2D, pts: Pt[], openings: Opening[], PT: number, v: View) {
  const n = pts.length;
  const keyOf = (a: Pt, b: Pt) => `${Math.min(a.x, b.x)},${Math.min(a.y, b.y)}-${Math.max(a.x, b.x)},${Math.max(a.y, b.y)}`;
  openings.filter(o => o.kind === 'existing' && o.edge).forEach(o => {
    for (let i = 0; i < n; i++) {
      const a = pts[i], b = pts[(i + 1) % n];
      if (keyOf(a, b) !== o.edge) continue;
      const horiz = a.y === b.y;
      const d = unitOf(a, b);
      const out = { x: d.y, y: -d.x }; // 시계방향 다각형: 바깥은 진행 방향 왼쪽
      const t0 = Math.min(o.from, o.to), t1 = Math.max(o.from, o.to);
      const p0 = horiz ? { x: t0, y: a.y } : { x: a.x, y: t0 }, p1 = horiz ? { x: t1, y: a.y } : { x: a.x, y: t1 };
      const w = PT * 1.5;
      const q = [p0, p1, add(p1, mul(out, w)), add(p0, mul(out, w))].map(p => ({ x: v.ox + p.x * v.scale, y: v.oy + p.y * v.scale }));
      ctx.save();
      ctx.beginPath(); q.forEach((p, k) => (k ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y))); ctx.closePath();
      ctx.fillStyle = '#ECEFF1'; ctx.fill();
      ctx.clip();
      ctx.strokeStyle = '#78909C'; ctx.lineWidth = 1;
      const xs = q.map(p => p.x), ys = q.map(p => p.y);
      const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
      for (let s = minX - (maxY - minY); s < maxX; s += 7) { ctx.beginPath(); ctx.moveTo(s, maxY); ctx.lineTo(s + (maxY - minY), minY); ctx.stroke(); }
      ctx.restore();
      ctx.save();
      ctx.beginPath(); q.forEach((p, k) => (k ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y))); ctx.closePath();
      ctx.strokeStyle = '#546E7A'; ctx.lineWidth = 1.2; ctx.stroke();
      ctx.restore();
    }
  });
}

/** 다각형 외곽선 (패널 바깥면) */
export function drawOutline(ctx: CanvasRenderingContext2D, pts: Pt[], v: View) {
  ctx.beginPath();
  pts.forEach((p, i) => {
    const x = v.ox + p.x * v.scale, y = v.oy + p.y * v.scale;
    if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y);
  });
  ctx.closePath();
  ctx.strokeStyle = '#000'; ctx.lineWidth = 1.5; ctx.stroke();
}

/**
 * 메인 화면 하리(바깥 박스 기준, 상단·우측 위치는 두께 더함) → 엔진 하리(박스 시작 위치, 가로=벽 방향 폭, 세로=빈 공간 깊이)
 */
export function mainHarisToEngine(haris: Hari[], H: number, V: number, PT: number): Hari[] {
  return haris.map(h => {
    if (h.hariType === 'corner') return { ...h };
    const ws = h.wallSide;
    const off = ws === 'top' || ws === 'right' ? PT : 0;
    const pos = off + (h.position ?? 0);
    // 메인: top/bottom은 width=벽 방향, height=돌출 / left/right는 width=돌출(x), height=벽 방향(y)
    const along = ws === 'top' || ws === 'bottom' ? h.width : (h.height ?? 0);
    const depth = ws === 'top' || ws === 'bottom' ? (h.height ?? 0) : h.width;
    return { ...h, position: pos, width: along, height: depth - PT };
  });
}

/** 메인 화면 문 → 외벽 개구부 (문 + 좌우 사이드 패널 전체) */
/** 문 → 개구부: 문 영역(좌 패널+문+우 패널)도 패널 한 장처럼 간격 4를 차지 (이전 시스템 실제 기록 기준) */
export function doorsToOpenings(doors: Door[], PT: number, space = 4): Opening[] {
  return doors.map(d => {
    const off = d.wallSide === 'top' || d.wallSide === 'right' ? PT : 0;
    const from = off + d.position;
    const V: Record<number, ['yangsu' | 'eumsu', 'yangsu' | 'eumsu']> = { 1: ['yangsu', 'eumsu'], 2: ['eumsu', 'yangsu'], 3: ['eumsu', 'eumsu'], 4: ['yangsu', 'yangsu'] };
    const v = d.panelVariant ? V[d.panelVariant] : null;
    return { side: d.wallSide, from, to: from + d.leftPanel + d.width + d.rightPanel + space, kind: 'door', id: d.id, ...(v ? { lo: v[0], hi: v[1] } : {}) };
  });
}
